/**
 * --------------------------------------------------------------------------
 * CoreUI password-input.js
 * Licensed under MIT (https://github.com/coreui/coreui/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import BaseComponent from './base-component.js'
import EventHandler from './dom/event-handler.js'
import SelectorEngine from './dom/selector-engine.js'
import {
  createControlGroupAction, ensureControlGroup, releaseControlGroup, type ControlGroup
} from './util/form-control-group.js'
import { PASSWORD_HIDE_ICON, PASSWORD_SHOW_ICON } from './util/icons.js'
import { sanitizeByConfig, type SanitizerAllowList, SVGAllowlist } from './util/sanitizer.js'
import { defineJQueryPlugin, jQueryDispatch } from './util/index.js'

/**
 * Constants
 */

const NAME = 'password-input'
const DATA_KEY = 'coreui.password-input'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const EVENT_CLICK = `click${EVENT_KEY}`
const EVENT_FOCUS = `focus${EVENT_KEY}`

const CLASS_NAME_ACTION = 'form-control-action'
const CLASS_NAME_PASSWORD_INPUT = 'password-input'

const SELECTOR_DATA_PASSWORD_INPUT = '[data-coreui-password-input]'

interface PasswordInputConfig {
  allowList: SanitizerAllowList
  ariaToggleLabel: string
  hideIcon: string
  sanitize: boolean
  sanitizeFn: ((unsafeHtml: string) => string) | null
  showIcon: string
}

const Default: PasswordInputConfig = {
  allowList: SVGAllowlist,
  ariaToggleLabel: 'Toggle password visibility',
  hideIcon: PASSWORD_HIDE_ICON,
  sanitize: true,
  sanitizeFn: null,
  showIcon: PASSWORD_SHOW_ICON
}

const DefaultType = {
  allowList: 'object',
  ariaToggleLabel: 'string',
  hideIcon: 'string',
  sanitize: 'boolean',
  sanitizeFn: '(null|function)',
  showIcon: 'string'
}

/**
 * Class definition
 */

class PasswordInput extends BaseComponent {
  // The component only ever binds to an <input>
  protected declare _element: HTMLInputElement
  protected declare _config: PasswordInputConfig
  private _group: ControlGroup | null = null
  private _hostType: string | null = null
  private _toggleElement: HTMLButtonElement | null = null

  constructor(element: string | Element, config?: Partial<PasswordInputConfig>) {
    super(element, config)

    this._hostType = this._element.getAttribute('type')
    this._createToggle()
    this._updateToggleState()
    this._updateDisabledState()
  }

  // Getters
  static override get Default(): typeof Default {
    return Default
  }

  static override get DefaultType(): typeof DefaultType {
    return DefaultType
  }

  static override get NAME(): string {
    return NAME
  }

  // Public
  toggle(): any {
    if (this._element.matches(':disabled')) {
      this._updateDisabledState()
      return
    }

    this._element.type = this._element.type === 'password' ? 'text' : 'password'
    this._updateToggleState()
  }

  override dispose(): void {
    if (!this._element) {
      return
    }

    if (this._toggleElement) {
      EventHandler.off(this._toggleElement, EVENT_KEY)
      this._toggleElement.remove()
    }

    if (this._group) {
      this._group.element.classList.remove(CLASS_NAME_PASSWORD_INPUT)
      releaseControlGroup(this._element, this._group)
    }

    if (this._hostType === null) {
      this._element.removeAttribute('type')
    } else {
      this._element.setAttribute('type', this._hostType)
    }

    super.dispose()
  }

  // Private
  // The button is the component's, so the frame that lays it out can be too:
  // the markup is a plain form control and the author writes neither.
  _createToggle(): void {
    this._group = ensureControlGroup(this._element)
    this._group.element.classList.add(CLASS_NAME_PASSWORD_INPUT)

    this._toggleElement = createControlGroupAction({
      className: CLASS_NAME_ACTION,
      icon: this._config.showIcon,
      label: this._config.ariaToggleLabel,
      sanitizeIcon: (icon: string) => sanitizeByConfig(icon, this._config)
    })

    EventHandler.on(this._toggleElement, EVENT_CLICK, () => this.toggle())
    EventHandler.on(this._element, EVENT_FOCUS, () => this._updateDisabledState())
    this._group.element.append(this._toggleElement)
  }

  _updateDisabledState(): void {
    if (this._toggleElement) {
      this._toggleElement.disabled = this._element.disabled
    }
  }

  // The toggle reflects the input's type: the pressed state for assistive
  // technology, and the icon for everyone else.
  _updateToggleState(): any {
    if (!this._toggleElement) {
      return
    }

    const visible = this._element.type === 'text'

    this._toggleElement.setAttribute('aria-pressed', visible ? 'true' : 'false')
    this._toggleElement.innerHTML = sanitizeByConfig(visible ? this._config.hideIcon : this._config.showIcon, this._config)
  }

  // Static
  static _initializeDataApi(): void {
    for (const element of SelectorEngine.find(SELECTOR_DATA_PASSWORD_INPUT)) {
      PasswordInput.getOrCreateInstance(element)
    }
  }

  // Static
  static jQueryInterface(this: any, config: any): void {
    return jQueryDispatch(this, PasswordInput, config, element => [element])
  }
}

/**
 * Data API implementation
 */

EventHandler.on(document, `DOMContentLoaded${EVENT_KEY}${DATA_API_KEY}`, () => {
  PasswordInput._initializeDataApi()
})

/**
 * jQuery
 */

defineJQueryPlugin(PasswordInput)

export default PasswordInput
export type { PasswordInputConfig }
